import { useState, useEffect, useRef } from "react";
import { T } from "./theme";
import LoadingSpinner from "./components/LoadingSpinner";

function maskEmail(e) {
  if (!e) return "";
  const [u, d] = e.split("@");
  return (u || "").slice(0, 2) + "***@" + (d || "");
}

export default function Login({ onLogin }) {
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [error, setError]       = useState("");
  const [loading, setLoading]   = useState(false);
  const [syncMsg, setSyncMsg]   = useState("");
  const pollRef                 = useRef(null);

  useEffect(() => {
    const last = localStorage.getItem("lastEmail");
    if (last) setEmail(last);
  }, []);

  useEffect(() => {
    if (loading) {
      pollRef.current = setInterval(async () => {
        try {
          const r = await fetch("/api/sync-status", { credentials: "include" });
          if (r.ok) { const d = await r.json(); if (d.message) setSyncMsg(d.message); }
        } catch {}
      }, 2000);
    } else {
      clearInterval(pollRef.current); setSyncMsg("");
    }
    return () => clearInterval(pollRef.current);
  }, [loading]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { setError("Preencha email e senha."); return; }
    setLoading(true); setError(""); setSyncMsg("Conectando ao HFocus...");
    try {
      const r = await fetch("/api/connect", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });
      const d = await r.json();
      if (d.success) {
        localStorage.setItem("lastEmail", email);
        const initials = email.split("@")[0].split(".").map(p => p[0]?.toUpperCase() || "").join("").slice(0, 2) || email.slice(0, 2).toUpperCase();
        onLogin({ nome: d.nome || "Usuario", email: maskEmail(email), role: d.role || "viewer", unidade: d.unidade || "", avatar: initials });
      } else {
        setError(d.error || "Credenciais invalidas ou acesso negado.");
        setLoading(false);
      }
    } catch {
      setError("Erro de conexao. Verifique sua internet.");
      setLoading(false);
    }
  };

  const [emailFocused, setEmailFocused] = useState(false);
  const [passFocused, setPassFocused] = useState(false);

  const getInputStyle = (focused) => ({
    width: "100%",
    padding: T.inputPaddingWithIcon,
    borderRadius: T.inputRadius,
    background: focused ? T.bgCard : T.bgInput,
    border: focused ? `1px solid ${T.accent}` : "1px solid transparent",
    boxShadow: focused ? `0 0 0 3px ${T.accentDim}` : "none",
    color: T.textHi,
    fontSize: 15,
    fontFamily: T.fontBody,
    boxSizing: "border-box",
    outline: "none",
    transition: T.transitionFast,
  });

  const [btnHovered, setBtnHovered] = useState(false);

  return (
    <div style={{
      minHeight: "100vh",
      background: T.bgBase,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 24,
      fontFamily: T.fontBody,
      backgroundImage: `radial-gradient(circle at top right, ${T.accentDim}, transparent 40%), radial-gradient(circle at bottom left, rgba(20,184,166,0.08), transparent 40%)`
    }}>
      <div style={{
        width: "100%", maxWidth: 420,
        background: T.bgCard,
        border: `1px solid ${T.border}`,
        borderRadius: 28,
        boxShadow: "0 20px 60px rgba(0,0,0,0.08)",
        padding: "48px 48px",
        animation: "fadeSlideIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards",
      }}>
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <div style={{
            width: 56, height: 56, borderRadius: 16,
            background: T.gradientAccent,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            marginBottom: 20,
            boxShadow: `0 8px 24px ${T.primaryGlow}`,
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
            </svg>
          </div>
          <div style={{ fontSize: T.titleSize, fontWeight: T.titleWeight, fontFamily: T.fontTitle, color: T.textHi, letterSpacing: "-0.03em" }}>TrueNPS</div>
          <div style={{ fontSize: 13, color: T.textLow, marginTop: 6, fontWeight: 500 }}>Acesse sua conta para continuar</div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {/* Email */}
          <div>
            <label style={{ display: "block", fontSize: 12, color: T.textMid, marginBottom: 8, fontWeight: 600, fontFamily: T.fontBody }}>
              Email HFocus
            </label>
            <div style={{ position: "relative" }}>
              <span style={{ position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)", color: emailFocused ? T.accent : T.textLow, display: "flex", pointerEvents: "none", transition: T.transitionFast }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="4"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
              </span>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                onFocus={() => setEmailFocused(true)} onBlur={() => setEmailFocused(false)}
                placeholder="seu@hfocus.com.br" autoComplete="email" required style={getInputStyle(emailFocused)}/>
            </div>
          </div>

          {/* Password */}
          <div>
            <label style={{ display: "block", fontSize: 12, color: T.textMid, marginBottom: 8, fontWeight: 600, fontFamily: T.fontBody }}>
              Senha
            </label>
            <div style={{ position: "relative" }}>
              <span style={{ position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)", color: passFocused ? T.accent : T.textLow, display: "flex", pointerEvents: "none", transition: T.transitionFast }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="4"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              </span>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                onFocus={() => setPassFocused(true)} onBlur={() => setPassFocused(false)}
                placeholder="••••••••" autoComplete="current-password" required style={getInputStyle(passFocused)}/>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div style={{
              display: "flex", alignItems: "center", gap: 10, padding: "12px 16px",
              borderRadius: T.inputRadius,
              background: `${T.red}10`, border: `1px solid ${T.red}30`,
              color: T.red, fontSize: 13, fontWeight: 500,
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ flexShrink: 0 }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              {error}
            </div>
          )}

          {/* Sync status */}
          {loading && syncMsg && (
            <div style={{ fontSize: 13, color: T.textLow, textAlign: "center", padding: "4px 0", fontWeight: 500 }}>
              {syncMsg}
            </div>
          )}

          {/* Submit */}
          <button type="submit" disabled={loading}
            onMouseEnter={() => setBtnHovered(true)}
            onMouseLeave={() => setBtnHovered(false)}
            style={{
              width: "100%", padding: "16px", borderRadius: T.radiusSm,
              background: loading ? T.accentDim : T.gradientAccent,
              border: "none", color: loading ? T.accent : "#fff",
              fontSize: 15, fontWeight: 700, fontFamily: T.fontBody, letterSpacing: "0.01em",
              cursor: loading ? "not-allowed" : "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
              marginTop: 8,
              transform: btnHovered && !loading ? "translateY(-2px)" : "none",
              boxShadow: btnHovered && !loading ? "0 8px 24px rgba(99,102,241,0.35)" : "0 2px 8px rgba(99,102,241,0.25)",
              transition: T.transitionFast,
              outline: "none",
            }}>
            {loading ? (
              <>
                <LoadingSpinner size="sm" />
                Conectando...
              </>
            ) : "Acessar Plataforma"}
          </button>
        </form>

        <p style={{ fontSize: 12, color: T.textLow, textAlign: "center", marginTop: 32, lineHeight: 1.6, fontWeight: 500 }}>
          Ambiente restrito.<br/>Seus dados estao protegidos.
        </p>
      </div>
    </div>
  );
}
